import { ConnectProto } from "@racoon/api";
import { Namespace, useT } from "@racoon/i18n";
import { Button, useModal } from "@racoon/ui";

import { useRouteJobsModalOutsideRouter } from "~/pages/dashboard/routes/hooks/useRouteJobsModal";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";

import { MswReportBody } from "../ui/msw-report.body";
import { LiveJob } from "../ui/live-job";
import { MswReportHeader } from "../ui/msw-report.header";
import { OrderAttempts } from "../ui/order-attempts";
import { createEditedJobs } from "./edited-jobs";

/** Opens a job's report, any job type; ↑/↓ step through the other jobs of `jobs`. */
export const usePhotoReportModal = () => {
  const { openModal, closeModal } = useModal();
  const t = useT();
  // Read here, inside the router: the modal provider renders outside it.
  const { activeCompanyId } = useAuthSession();
  // The report is a modal: the route card it opens has no router of its own to navigate
  const { openRouteJobs } = useRouteJobsModalOutsideRouter();

  return (jobs: ConnectProto.Job[], initial: ConnectProto.Job) => {
    const items = [...jobs];
    if (!items.some((job) => job.id === initial.id)) items.unshift(initial);
    const edited = createEditedJobs();

    // Container edits are written only with a review status, so closing with any
    // job still a draft asks first. The confirm stacks on top of the report.
    const requestClose = () => {
      if (!edited.hasDrafts()) return true;
      const confirmId = openModal({
        size: "md",
        showClose: false,
        // Body, not `description`: an empty body would stack the header and footer rules.
        title: (
          <span className="text-base font-semibold leading-snug text-text-body">
            {t(Namespace.PhotoReport, "unsavedClose.title")}
          </span>
        ),
        body: (
          <p className="text-sm leading-snug text-icon-secondary">
            {t(Namespace.PhotoReport, "unsavedClose.description")}
          </p>
        ),
        footer: (
          <>
            <Button type="button" size="sm" variant="outline" onClick={() => closeModal(confirmId)}>
              {t(Namespace.PhotoReport, "unsavedClose.cancel")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="solidRed"
              onClick={() => {
                closeModal(confirmId);
                closeModal(reportId);
              }}
            >
              {t(Namespace.PhotoReport, "unsavedClose.confirm")}
            </Button>
          </>
        ),
      });
      return false;
    };

    const reportId = openModal({
      size: "custom",
      width: { md: "min(75rem, 95vw)" },
      showClose: true,
      closeButtonTop: "top-5",
      className: "overflow-hidden !gap-0 bg-white",
      headerClassName: "!pb-4",
      // The close cross, Escape and a backdrop click all land here.
      onRequestClose: requestClose,
      navigation: {
        items,
        initialIndex: items.findIndex((job) => job.id === initial.id),
        getItemId: (job) => String(job.id),
        renderTitle: (job) => (
          <LiveJob edited={edited} job={job}>
            {(live) => <MswReportHeader job={live} />}
          </LiveJob>
        ),
        renderBody: (job) => (
          <LiveJob edited={edited} job={job}>
            {(live) => (
              // Keyed by job: the arrows reset the attempt picked
              <OrderAttempts key={String(live.id)} job={live}>
                {(attempt) => (
                  // Another attempt's edits show and save as the opened job's do
                  <LiveJob edited={edited} job={attempt}>
                    {(shown) => (
                      <MswReportBody
                        job={shown}
                        ownerOrganizationId={activeCompanyId}
                        isDraft={edited.isDraft(shown)}
                        onChange={edited.set}
                        onDraft={edited.setDraft}
                        onOpenRoute={openRouteJobs}
                      />
                    )}
                  </LiveJob>
                )}
              </OrderAttempts>
            )}
          </LiveJob>
        ),
      },
    });
  };
};
